/* global globalThis */
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setInternationalFilters,
  resetInternationalFilters,
} from "../redux/slices/internationalFilterSlice";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import CommonFilterInput from "../components/common/commonFilters/CommonFilterInput";
import CommonTable from "../components/common/table/commonTable";
import CommonSearchBar from "../components/common/searchBar/CommonSearchBar";
import PageHeader from "../components/common/PageHeader";
import CloseIcon from "@mui/icons-material/Close";
import "./Searchpage.css";
import { getInternationalColumns } from "../components/common/table/InternationalColumns";
import TrackEnrollmentModal from "../components/modals/TrackEnrollmentModal";

const MainContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  flexGrow: 1,
  padding: 0,
  margin: 0,
  width: open ? "calc(100% - 240px)" : "100%",
  marginLeft: open ? "240px" : "0",
  minHeight: "80vh",
  backgroundColor: "#F7F8FB",
  overflow: "hidden !important",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

/**
 * Estimates the diagonal screen size in inches based on screen resolution and DPI
 * @returns {number} Diagonal screen size in inches
 */
function getScreenInches() {
  const { width, height } = globalThis.window.screen;
  const dpi = globalThis.window.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const InternationalSubmission = () => {
  const dispatch = useDispatch();
  const internationalFilters = useSelector(
    (state) => state.internationalFilters,
  );

  const {
    searchTerm,
    memberFilter,
    detailsFilter,
    statusFilter,
    sorting,
    memberSearch,
    detailsSearch,
    statusSearch,
  } = internationalFilters;

  const [filtersVisible, setFiltersVisible] = useState(true);
  const [isTrackEnrollmentOpen, setIsTrackEnrollmentOpen] = useState(false);
  const [trackOrdersData, setTrackOrdersData] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [computedTableHeight, setComputedTableHeight] = useState(0);
  const resultsContainerRef = useRef(null);
  const [containerScale, setContainerScale] = useState(1);

  const ui = {
    containerScale,
    computedTableHeight,
    resultsContainerRef,
    filtersVisible,
    setFiltersVisible,
  };

  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  useEffect(() => {
    const computeHeight = () => {
      const el = resultsContainerRef.current;
      if (!el) return;
      const vh =
        globalThis.window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;

      const isSmallLaptop =
        globalThis.window.innerWidth <= 1440 &&
        globalThis.window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      const h = Math.max(200, Math.floor(vh - top - bottomPadding + extra));
      setComputedTableHeight(h);
    };

    computeHeight();
    globalThis.window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      if (document.body) ro.observe(document.body);
    } catch (error) {
      console.debug("ResizeObserver loop limit exceeded:", error);
    }
    return () => {
      globalThis.window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, [filtersVisible]);

  const onFilterSearch = (term, type) => {
    if (type === "MEMBER")
      dispatch(setInternationalFilters({ memberSearch: term }));
    else if (type === "DETAILS")
      dispatch(setInternationalFilters({ detailsSearch: term }));
    else if (type === "STATUS")
      dispatch(setInternationalFilters({ statusSearch: term }));
  };

  // Sample data
  const [data] = useState([
    {
      id: "GRM 29841",
      member: "John Smith",
      vendor: "GRM",
      memberId: "BCBS-10296372",
      personCode: "01",
      submittedOn: "03/15/26",
      status: "Submitted",
      patientCareAdvocate: "Tammy Baeso",
    },
    {
      id: "GRM 29841",
      member: "Emily Johnson",
      vendor: "GRM",
      memberId: "BCBS-10296372",
      personCode: "01",
      submittedOn: "03/15/26",
      status: "Pending",
      patientCareAdvocate: "Tammy Baeso",
    },
    {
      id: "GRM 29841",
      member: "Michael Brown",
      vendor: "GRM",
      memberId: "BCBS-10296372",
      personCode: "01",
      submittedOn: "03/15/26",
      status: "Cancelled",
      patientCareAdvocate: "Tammy Baeso",
    },
  ]);

  // Extract unique values for filters
  const members = [...new Set(data.map((item) => item.member))];
  const detailsOptions = [
    ...new Set(data.map((item) => item.patientCareAdvocate)),
  ];
  const statuses = ["Submitted", "Pending", "Cancelled"];

  // Filter data based on search and filter values
  const filteredData = data.filter((item) => {
    const matchesSearch =
      searchTerm === "" ||
      Object.values(item).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      );

    const matchesMember =
      memberFilter.length === 0 || memberFilter.includes(item.member);
    const matchesDetails =
      detailsFilter.length === 0 ||
      detailsFilter.includes(item.patientCareAdvocate);
    const matchesStatus =
      statusFilter.length === 0 || statusFilter.includes(item.status);

    return matchesSearch && matchesMember && matchesDetails && matchesStatus;
  });

  const handleRefreshClick = React.useCallback((rowData) => {
    const ordersToSend =
      rowData.orders && rowData.orders.length > 0
        ? rowData.orders.map((order) => ({
            ...order,
            memberName: rowData.member,
            vendor: rowData.vendor,
            submittedOn: rowData.submittedOn,
            enrollmentId: rowData.memberId,
            trackingId:
              order.trackingId ||
              `TRK-${rowData.id?.replace("GRM ", "") || "0000"}`,
          }))
        : [
            {
              orderId: rowData.id
                ? `#${rowData.id.toString().replace("GRM ", "")}`
                : "",
              status: rowData.status || "Submitted",
              memberName: rowData.member,
              vendor: rowData.vendor,
              submittedOn: rowData.submittedOn,
              enrollmentId: rowData.memberId,
              trackingId: `TRK-${rowData.id?.replace("GRM ", "") || "0000"}`,
            },
          ];
    setTrackOrdersData(ordersToSend);
    setIsTrackEnrollmentOpen(true);
  }, []);

  const columns = React.useMemo(
    () => getInternationalColumns(handleRefreshClick),
    [handleRefreshClick],
  );

  const activeFilterCount =
    memberFilter.length + detailsFilter.length + statusFilter.length;

  const handleClearFilters = () => {
    dispatch(resetInternationalFilters());
  };

  return (
    <Box
      sx={{
        display: "flex",
        overflow: "hidden !important",
        backgroundColor: "transparent",
      }}
    >
      <MainContent open={false} className="search-page">
        <TrackEnrollmentModal
          open={isTrackEnrollmentOpen}
          onClose={() => setIsTrackEnrollmentOpen(false)}
          orders={trackOrdersData}
        />
        <Box
          className="search-page-container"
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: isMobile ? 2 : "0 18px",
          }}
        >
          <div
            className="search-page-content"
            style={{
              transform: `scale(${ui.containerScale})`,
              transformOrigin: "top left",
              width: ui.containerScale < 1 ? "111%" : "100%",
              minHeight: "92vh",
            }}
          >
            <Box
              sx={{
                "& .page-header": {
                  margin: "10px 0 10px 0",
                  padding: 0,
                  "& h1": {
                    margin: 0,
                    lineHeight: "36px",
                  },
                },
              }}
            >
              <PageHeader title="International Submission" />
            </Box>

            <Box className="search-top-row">
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  width: isMobile ? "100%" : "auto",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: isMobile ? "100%" : "280px",
                    marginRight: isMobile ? "8px" : "32px",
                  }}
                >
                  <CommonSearchBar
                    onSearch={(val) =>
                      dispatch(setInternationalFilters({ searchTerm: val }))
                    }
                    value={searchTerm}
                    placeholder="Search by ID, member ID, patient care advocate"
                  />
                </div>

                {(() => {
                  let buttonBgColor;
                  if (activeFilterCount > 0) {
                    buttonBgColor = "#FAFAFF";
                  } else if (filtersVisible) {
                    buttonBgColor = "#ECECF1";
                  } else {
                    buttonBgColor = "#FFFFFF";
                  }
                  let buttonHoverBgColor;
                  if (filtersVisible) {
                    buttonHoverBgColor = "#ECECF1";
                  } else {
                    buttonHoverBgColor = "rgba(64, 60, 111, 0.04)";
                  }
                  return (
                    <Button
                      variant="outlined"
                      startIcon={
                        activeFilterCount > 0 && (
                          <Box
                            sx={{
                              backgroundColor: "#403C6F",
                              color: "white",
                              fontSize: "9px",
                              fontWeight: 400,
                              height: "16px",
                              minWidth: "16px",
                              borderRadius: "50%",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              lineHeight: 1,
                              "& > span": {
                                position: "relative",
                                top: "0.5px",
                                fontSize: "12px",
                                lineHeight: 1,
                              },
                            }}
                          >
                            <span>{activeFilterCount}</span>
                          </Box>
                        )
                      }
                      endIcon={
                        <FilterListRoundedIcon style={{ fontSize: "18px" }} />
                      }
                      onClick={() => setFiltersVisible(!filtersVisible)}
                      sx={{
                        height: "35px",
                        minWidth: "90px",
                        borderRadius: "48px",
                        border: "1px solid #E8E8E9",
                        padding: "6px 12px 6px 16px",
                        textTransform: "none",
                        color: "#403C6F",
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "20px",
                        letterSpacing: "0.1px",
                        gap: "4px",
                        backgroundColor: buttonBgColor,
                        "& .MuiButton-endIcon": {
                          marginLeft: "4px",
                          marginRight: "-4px",
                        },
                        "&:hover": {
                          borderColor: "#403C6F",
                          backgroundColor: buttonHoverBgColor,
                        },
                      }}
                    >
                      Filter
                    </Button>
                  );
                })()}

                {activeFilterCount > 0 &&
                  (isMobile ? (
                    <Box
                      onClick={handleClearFilters}
                      sx={{
                        height: "35px",
                        width: "35px",
                        minWidth: "35px",
                        borderRadius: "50%",
                        border: "1px solid #E8E8E9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        backgroundColor: "#FFFFFF",
                        color: "#403C6F",
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleClearFilters}
                      sx={{
                        height: "33px",
                        minWidth: "70px",
                        borderRadius: "48px",
                        border: "1px solid #66638C",
                        padding: "6px 10px",
                        textTransform: "none",
                        color: "#403C6F",
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "20px",
                        backgroundColor: "#FFFFFF",
                        "&:hover": {
                          borderColor: "#66638C",
                          backgroundColor: "#F7F8FB",
                        },
                      }}
                    >
                      Clear Filter
                    </Button>
                  ))}
              </div>
            </Box>

            {filtersVisible && (
              <Box className="search-filters-row">
                <Box className="common-filters">
                  {/* Member Filter */}
                  <CommonFilterInput
                    label="Member"
                    placeholder="Filter by Member"
                    value={memberFilter}
                    onChange={(val) =>
                      dispatch(setInternationalFilters({ memberFilter: val }))
                    }
                    options={members}
                    type="select"
                    multiple={true}
                    compact={isMobile}
                    showLabel={true}
                    onSearch={onFilterSearch}
                    filterType="MEMBER"
                    searchValue={memberSearch}
                  />

                  {/* Patient care advocate Filter */}
                  <CommonFilterInput
                    label="Patient care advocate"
                    placeholder="Filter by Patient care advocate"
                    value={detailsFilter}
                    onChange={(val) =>
                      dispatch(setInternationalFilters({ detailsFilter: val }))
                    }
                    options={detailsOptions}
                    type="select"
                    multiple={true}
                    compact={isMobile}
                    showLabel={true}
                    onSearch={onFilterSearch}
                    filterType="DETAILS"
                    searchValue={detailsSearch}
                  />

                  {/* Status Filter */}
                  <CommonFilterInput
                    label="Status"
                    placeholder="Filter by Status"
                    value={statusFilter}
                    onChange={(val) =>
                      dispatch(setInternationalFilters({ statusFilter: val }))
                    }
                    options={statuses}
                    type="select"
                    multiple={true}
                    compact={isMobile}
                    showLabel={true}
                    onSearch={onFilterSearch}
                    filterType="STATUS"
                    searchValue={statusSearch}
                  />
                </Box>
              </Box>
            )}

            {/* Results count aligned to the right */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#403C6F",
                  fontSize: "12px",
                  fontWeight: 500,
                  opacity: 0.8,
                }}
              >
                Total {filteredData.length} Records
              </Typography>
            </Box>

            {/* Data Table using CommonTable */}
            <Box
              className="search-results-container"
              ref={resultsContainerRef}
              sx={{
                flex: 1,
                overflow: "hidden",
                "& .MuiTableHead-root .MuiTableRow-root": {
                  backgroundColor: "#CFCEDA",
                },
                "& .MuiTableCell-head": {
                  color: "#403C6F !important",
                  fontWeight: 700,
                  fontSize: "13px",
                  lineHeight: "130%",
                },
                "& .MuiTableCell-body": {
                  color: "#403C6F",
                  fontSize: "13px",
                },
                "& .international-submission-table": {
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "none",
                },
                "& .common-table-header-cell": {
                  color: "#403C6F !important",
                  backgroundColor: "#CFCEDA !important",
                },
                "& .common-table .MuiTableHead .MuiTableCell-root": {
                  color: "#403C6F !important",
                  backgroundColor: "#CFCEDA !important",
                },
              }}
            >
              <CommonTable
                columns={columns}
                data={filteredData}
                filtersVisible={filtersVisible}
                className="international-submission-table"
                pageSize={25}
                enableSorting={true}
                tableHeight={computedTableHeight}
                sorting={sorting}
                onSortingChange={(val) =>
                  dispatch(setInternationalFilters({ sorting: val }))
                }
              />
            </Box>
          </div>
        </Box>
      </MainContent>
    </Box>
  );
};

export default InternationalSubmission;
